import { FilterTypes } from '@aboutyou/backbone/endpoints/filters/filters'
import { useTranslation } from 'helpers/hooks/useTranslation'
import React, { FC, memo } from 'react'
import { SlugifiedAttributesFilterItemWithValues } from 'services/providers/bapi/entities/filters/normalize'
import {
  FilterGroup,
  PreferredFilterType,
} from 'services/providers/bapi/entities/filters/types'
import { SpecialSizesSelectorSection } from 'sharedComponents/ProductStream/filters/FilterSections'
import { attributeFilterPropsAreEqual } from 'sharedComponents/ProductStream/helpers'
import FilterViewStore from 'store/sidecar/FilterViewStore'
import { useFilterStore } from 'store/sidecar/ProductStreamContext'
import UnfoldingFilterBox from 'uiComponents/organisms/Filters/UnfoldingFilterBox'
import {
  useTrackOnCloseFilter,
  useTrackOnOpenFilter,
} from '../../../helpers/tracking/hooks/trackFilter'
import { translateFilterGroupName } from '../util/transformFilters'
import SimpleHeadline from './AdvancedFilters/SimpleHeadline'
import SizeFilter from './AdvancedFilters/SizeFilter'

const AttributeFilter: FC<SlugifiedAttributesFilterItemWithValues> = memo(
  function AttributeFilter(filter) {
    const { t } = useTranslation()
    switch (filter.slug) {
      case 'CATEGORY_SHOP_FILTER_SIZES_GROUP_NAME_SHOES':
        return (
          <SimpleHeadline title={translateFilterGroupName(filter.name, t)} />
        )
      case 'specialSizesFilter':
        return <SpecialSizesSelectorSection filter={filter} />
      case 'categoryShopFilterSizes':
      case 'defFemaleInch':
      case 'defFemaleInt':
      case 'defFemaleKonf':
      case 'defFemaleCups':
      case 'defFemaleEur':
      case 'defFemaleRings':
      case 'defFemaleCm':
      case 'defMaleInch':
      case 'defMaleKonf':
      case 'defMaleInt':
      case 'defMaleEur':
      case 'defMaleRings':
      case 'defMaleCm':
      case 'defMaleCollar':
      case 'defKidsEur':
      case 'defKidsInt':
      case 'defKidsCm':
      case 'defFemaleHead':
      case 'defMaleHead':
      case 'defKidsHead':
      case 'fallback_generic_size':
        return <SizeFilter filter={filter} />
      default:
        return null
    }
  },
  attributeFilterPropsAreEqual,
)

function getActiveFiltersCount(
  filterGroup: FilterGroup,
  filterStore: FilterViewStore,
) {
  const allFilterSlugs = [
    ...new Set(filterGroup.filters.map(filter => filter.slug)),
  ]
  return allFilterSlugs.reduce((sum: number, filterSlug: string) => {
    const selectedFilterCountForSlug =
      filterStore.selectedFilters[filterSlug as PreferredFilterType]?.length

    if (selectedFilterCountForSlug) {
      sum = sum + selectedFilterCountForSlug
    }
    return sum
  }, 0)
}

const GroupFilter: FC<{ filterGroup: FilterGroup }> = ({ filterGroup }) => {
  const { t } = useTranslation()
  const filterStore = useFilterStore()
  const onCloseFilterTracking = useTrackOnCloseFilter()
  const onOpenFilterTracking = useTrackOnOpenFilter()
  const trackOnClose = () => {
    onCloseFilterTracking('sizes', 'quick_filters')
  }
  const trackOnOpen = () => {
    onOpenFilterTracking('sizes', 'quick_filters')
  }

  const handleResetVariantB = () => {
    filterGroup.filters.forEach(filter => {
      filterStore.clearFilterGroup(filter.slug as PreferredFilterType)
    })
  }

  const activeFiltersCount = getActiveFiltersCount(filterGroup, filterStore)

  return (
    <UnfoldingFilterBox
      label={t('Größe')}
      dataTestId={`Group_Filter_${filterGroup.filterGroupSlug}`}
      activeFiltersCount={activeFiltersCount}
      onReset={handleResetVariantB}
      onClose={trackOnClose}
      onOpen={trackOnOpen}
    >
      {filterGroup.filters.map(filter => {
        if (filter.type === FilterTypes.ATTRIBUTES) {
          return <AttributeFilter {...filter} key={filter.slug} />
        }
      })}
    </UnfoldingFilterBox>
  )
}

export default GroupFilter
